import React from 'react';
import _ from "lodash";
import { withRouter } from "react-router";
import { API } from "aws-amplify";
import { AssetButtonFilter, AssetButtonFilterValueCellClear } from '@/icons/Assets';

import SupportToolsTenants from "./SupportToolsTenants";
import SupportToolsAgents from "./SupportToolsAgents";
import SupportToolsConsortiums from "./SupportToolsConsortiums";

const TENANT_LABELS = [
    { key: 'engage',   title: 'Engage' },
    { key: 'ready',    title: 'Ready' },
    { key: 'pss',      title: 'PSS' },
    { key: 'churn',    title: 'Churn' },
    { key: 'internal', title: 'Internal' },
];

// Self-contained accessible filter widget.
// Keyboard contract:
//   Trigger  Enter/Space → open dropdown, focus first item
//   Menu     ArrowDown/Up → move focus between items (wraps)
//   Menu     Enter/Space  → toggle item, keep dropdown open
//   Menu     Tab/Esc      → close dropdown, return focus to trigger
//   Chips    Tab (from trigger, dropdown closed) → focus rightmost chip ✕
//   Chip ✕   Enter/Space  → remove chip; if chips remain focus next rightmost;
//                           if no chips remain → open dropdown
//   No chips + Enter on trigger → open dropdown (normal flow)
function TenantTypeFilter({ filters, onToggle, searchRef }) {
    const [open, setOpen] = React.useState(false);
    const [focusedIdx, setFocusedIdx] = React.useState(0);
    const triggerRef = React.useRef();
    const menuRef = React.useRef();
    const chipRefs = React.useRef([]);
    // Keep chipRefs array length in sync with filters so stale entries never exist
    chipRefs.current = chipRefs.current.slice(0, filters.length);

    const openMenu = () => {
        setOpen(true);
        setFocusedIdx(0);
    };

    const closeMenu = (returnFocus = true) => {
        setOpen(false);
        if (returnFocus && triggerRef.current) triggerRef.current.focus();
    };

    // Focus the correct menu item whenever the menu opens or focusedIdx changes
    React.useEffect(() => {
        if (open && menuRef.current) {
            const items = menuRef.current.querySelectorAll('[role="option"]');
            if (items[focusedIdx]) items[focusedIdx].focus();
        }
    }, [open, focusedIdx]);

    // Close menu on outside click
    React.useEffect(() => {
        if (!open) return;
        const handleOutsideClick = (e) => {
            if (
                triggerRef.current && !triggerRef.current.contains(e.target) &&
                menuRef.current && !menuRef.current.contains(e.target)
            ) {
                setOpen(false);
            }
        };
        document.addEventListener('mousedown', handleOutsideClick);
        return () => document.removeEventListener('mousedown', handleOutsideClick);
    }, [open]);

    const handleTriggerKeyDown = (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            openMenu();
        } else if (e.key === 'Tab' && !e.shiftKey) {
            if (chipRefs.current.length > 0 && chipRefs.current[0]) {
                e.preventDefault();
                chipRefs.current[0].focus();
            }
            // no chips → let Tab fall through naturally to search
        }
    };

    const handleMenuKeyDown = (e) => {
        if (e.key === 'ArrowDown') {
            e.preventDefault();
            setFocusedIdx(i => (i + 1) % TENANT_LABELS.length);
        } else if (e.key === 'ArrowUp') {
            e.preventDefault();
            setFocusedIdx(i => (i - 1 + TENANT_LABELS.length) % TENANT_LABELS.length);
        } else if (e.key === 'Escape') {
            e.preventDefault();
            setOpen(false);
            requestAnimationFrame(() => {
                if (triggerRef.current) triggerRef.current.focus();
            });
        } else if (e.key === 'Tab') {
            e.preventDefault();
            setOpen(false);
            requestAnimationFrame(() => {
                if (chipRefs.current.length > 0 && chipRefs.current[0]) {
                    chipRefs.current[0].focus();
                } else if (searchRef && searchRef.current) {
                    searchRef.current.focus();
                }
            });
        }
    };

    const handleItemKeyDown = (e, key) => {
        if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            e.stopPropagation();
            onToggle(key);
            // keep open — useEffect will re-focus same index
        }
    };

    const handleChipKeyDown = (e, key, chipIdx) => {
        e.stopPropagation(); // prevent outer trigger from intercepting chip key events
        if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            onToggle(key);
            // After removal: focus next chip (same index, now shifted) or open menu
            // Double rAF: first frame React re-renders, second frame refs are populated
            requestAnimationFrame(() => requestAnimationFrame(() => {
                const len = chipRefs.current.length;
                if (len > 0) {
                    const nextFocus = chipRefs.current[Math.min(chipIdx, len - 1)];
                    if (nextFocus) nextFocus.focus();
                } else {
                    openMenu();
                }
            }));
        } else if (e.key === 'Tab' && !e.shiftKey) {
            const nextChip = chipRefs.current[chipIdx + 1];
            if (nextChip) {
                e.preventDefault();
                nextChip.focus();
            }
            // last chip → let Tab fall through naturally to search
        } else if (e.key === 'Tab' && e.shiftKey) {
            e.preventDefault();
            const prevChip = chipRefs.current[chipIdx - 1];
            if (prevChip) {
                prevChip.focus();
            } else {
                triggerRef.current.focus();
            }
        }
    };

    const activeLabel = filters.length
        ? `Tenant Type filter, ${filters.length} active`
        : 'Tenant Type filter';

    return (
        <div style={{ position: 'relative', display: 'inline-flex', alignItems: 'flex-start', height: '32px' }}>
            {/* Trigger */}
            <div
                ref={triggerRef}
                tabIndex={0}
                role="button"
                aria-haspopup="listbox"
                aria-expanded={open}
                aria-label={activeLabel}
                className={`filterButton navAction${filters.length ? ' isFiltered' : ''}`}
                onClick={() => open ? closeMenu(false) : openMenu()}
                onKeyDown={handleTriggerKeyDown}
            >
                <AssetButtonFilter />
                <div className="filterButtonTitle">Filter</div>
                {filters.map((key, i) => {
                    const label = TENANT_LABELS.find(l => l.key === key);
                    if (!label) return null;
                    return (
                        <div className="filterButtonValue" key={key}>
                            <div
                                className="filterButtonValueCell"
                                onClick={e => { e.stopPropagation(); onToggle(key); }}
                            >
                                <div className="filterButtonValueCellTitle">{label.title}</div>
                                <div
                                    role="button"
                                    aria-label={`Remove ${label.title} filter`}
                                    tabIndex={0}
                                    className="filterButtonValueCellClear"
                                    ref={el => { chipRefs.current[i] = el; }}
                                    onClick={e => { e.stopPropagation(); onToggle(key); }}
                                    onKeyDown={e => handleChipKeyDown(e, key, i)}
                                >
                                    <AssetButtonFilterValueCellClear />
                                </div>
                            </div>
                        </div>
                    );
                })}
            </div>

            {/* Dropdown — anchored to the outer wrapper's top so it always opens
                at the same vertical position regardless of how tall the trigger grows */}
            {open && (
                <div
                    ref={menuRef}
                    className="pullDownMenu"
                    role="listbox"
                    aria-label="Tenant Type filter options"
                    onKeyDown={handleMenuKeyDown}
                    style={{ position: 'absolute', top: '40px', left: 0, zIndex: 200, width: 'auto', minWidth: '140px', height: 'auto' }}
                >
                    {TENANT_LABELS.map((f, idx) => (
                        <div
                            key={f.key}
                            role="option"
                            tabIndex={-1}
                            aria-selected={filters.includes(f.key)}
                            className={`menuCell ${filters.includes(f.key) ? 'selected' : ''}`}
                            onClick={() => onToggle(f.key)}
                            onKeyDown={e => handleItemKeyDown(e, f.key)}
                        >
                            <div className="menuItemTitle">{f.title}</div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}

function SupportTools(props) {
    const TENANTS = "TENANTS";
    const AGENTS = "AGENTS";
    const CONSORTIUMS = "CONSORTIUMS";
    const [activeTab, setActiveTab] = React.useState(TENANTS);
    const [searchInput, setSearchInput] = React.useState('');
    const [agentSearchInput, setAgentSearchInput] = React.useState('');
    const [filters, setFilters] = React.useState([]);
    const filterRef = React.useRef();
    const searchRef = React.useRef();
    const agentSearchRef = React.useRef();
    const agentCountRef = React.useRef(0);
    const tenantCountRef = React.useRef(0);
    const [srMessage, setSrMessage] = React.useState('');
    const [hasAccess, setHasAccess] = React.useState(null); // null = checking, true = has access, false = denied
    const [isCheckingAccess, setIsCheckingAccess] = React.useState(true);

    // Used to skip filter announcements on initial mount and on tab-switch resets
    const filterHasMountedRef = React.useRef(false);
    const prevFiltersRef = React.useRef([]);
    // Mirror activeTab into a ref so the filter effect can read it without a dep
    const activeTabRef = React.useRef(activeTab);
    activeTabRef.current = activeTab;

    const simpleMode = window.__RUNTIME_CONFIG__?.AGENTS_SIMPLE_MODE === 'true';

    const announce = React.useCallback((msg) => {
        setSrMessage('');
        setTimeout(() => setSrMessage(msg), 50);
    }, []);

    const announceAgentSearch = React.useCallback(() => {
        const q = agentSearchInput.trim();
        if (q === '') {
            announce('Search cleared. Showing all results.');
        } else if (agentCountRef.current === 0) {
            announce(simpleMode ? 'No agents match your search.' : 'No agents or connectors match your search.');
        } else {
            announce(`Search completed. ${agentCountRef.current} results found.`);
        }
    }, [agentSearchInput, simpleMode, announce]);

    const announceTenantSearch = React.useCallback(() => {
        const q = searchInput.trim();
        if (q === '') {
            announce('Search cleared. Showing all results.');
        } else if (tenantCountRef.current === 0) {
            announce('No results found.');
        } else {
            announce(`Search completed. ${tenantCountRef.current} results found.`);
        }
    }, [searchInput, announce]);

    // Announce when tenant type filters change.
    // Reads tenantCountRef.current at effect time (always up-to-date — set by the child
    // synchronously during render, before effects fire).
    React.useEffect(() => {
        if (!filterHasMountedRef.current) {
            filterHasMountedRef.current = true;
            prevFiltersRef.current = filters;
            return;
        }
        // When switching tabs setFilters([]) fires; activeTabRef reflects the new tab already
        if (activeTabRef.current !== TENANTS) {
            prevFiltersRef.current = filters;
            return;
        }
        const prev = prevFiltersRef.current;
        prevFiltersRef.current = filters;
        const count = tenantCountRef.current;

        if (filters.length === 0) {
            announce(`All filters cleared. ${count} results found.`);
            return;
        }
        const addedKey = filters.find(k => !prev.includes(k));
        const removedKey = prev.find(k => !filters.includes(k));
        if (addedKey) {
            const label = TENANT_LABELS.find(l => l.key === addedKey)?.title || addedKey;
            announce(`${label} filter applied. ${count} results found.`);
        } else if (removedKey) {
            const label = TENANT_LABELS.find(l => l.key === removedKey)?.title || removedKey;
            announce(`${label} filter removed. ${count} results found.`);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filters]);

    const toggleFilter = (key) => {
        setFilters(prev =>
            prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key]
        );
    };


    const tabs = [
        TENANTS,
        AGENTS,
        CONSORTIUMS
    ];

    // Check access on mount by making a lightweight API call
    React.useEffect(() => {
        API.get('Core', '/api/v1/support-tools/tenants')
            .then(() => {
                setHasAccess(true);
                setIsCheckingAccess(false);
            })
            .catch(() => {
                setHasAccess(false);
                setIsCheckingAccess(false);
            });
    }, []);

    React.useEffect(() => {
        if (props.match.params && props.match.params.tab && _.indexOf(tabs, props.match.params.tab) >= 0) {
            setActiveTab(props.match.params.tab);
        }
        else {
            setActiveTab(TENANTS);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const getTabContents = () => {
        // eslint-disable-next-line default-case
        switch (activeTab) {
            case TENANTS:
                return (<SupportToolsTenants searchInput={searchInput} filters={filters} countRef={tenantCountRef} />);
            case AGENTS:
                return (<SupportToolsAgents searchInput={agentSearchInput} setSearchInput={setAgentSearchInput} countRef={agentCountRef} />);
            case CONSORTIUMS:
                return (<SupportToolsConsortiums />);
        }
    }

    const setTab = (tabKey) => {
        setActiveTab(tabKey);
        setSearchInput('');
        setAgentSearchInput('');
        setFilters([]);
        props.history.push("/support-tools/" + tabKey);
    }

    // Show loading state while checking access
    if (isCheckingAccess) {
        return (
            <div className="rootView supportToolsView">
                <style>
                    {`
                        @keyframes spin {
                            0% { transform: rotate(0deg); }
                            100% { transform: rotate(360deg); }
                        }
                    `}
                </style>
                <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
                    <div style={{
                        width: '48px',
                        height: '48px',
                        border: '4px solid rgba(255,255,255,0.1)',
                        borderTopColor: '#fff',
                        borderRadius: '50%',
                        animation: 'spin 1s linear infinite'
                    }} />
                </div>
            </div>
        );
    }

    // Show access denied if no permission
    if (hasAccess === false) {
        return (
            <div className="rootView supportToolsView">
                <div className="emptyState" style={{ padding: '40px 20px', textAlign: 'center' }}>
                    <div style={{ fontSize: '16px', fontWeight: '500', marginBottom: '8px' }}>
                        Access Denied
                    </div>
                    <div style={{ fontSize: '14px', opacity: 0.7 }}>
                        You do not have permission to access Support Tools.
                    </div>
                </div>
            </div>
        );
    }

    // Show full UI if access granted
    return (
        <div className="rootView supportToolsView">
            <div className="navBar">
                <div className="navTitle">
                    <div className="title">Support Tools</div>
                </div>

                {/*
                  WAI-ARIA tablist pattern: only the active tab has tabIndex={0};
                  inactive tabs have tabIndex={-1} and are reached with ArrowLeft/Right.
                  This means Tab exits the tablist immediately after the active tab
                  and moves to filter → search, satisfying the required sequence:
                  Tenants (active, focused) → Filter → Search → [Tab back in: Agents → Consortiums].
                  The tablist stays visually intact as one segmentedBar.
                */}
                <div className="actions" style={{ gap: '8px' }}>
                    <div
                        className={`segmentedBar index${_.indexOf(tabs, activeTab)}`}
                        style={{ width: '300px', order: 1 }}
                        role="tablist"
                        aria-label="Support Tools sections"
                        onKeyDown={e => {
                            const idx = _.indexOf(tabs, activeTab);
                            if (e.key === 'ArrowRight') {
                                e.preventDefault();
                                setTab(tabs[(idx + 1) % tabs.length]);
                            } else if (e.key === 'ArrowLeft') {
                                e.preventDefault();
                                setTab(tabs[(idx - 1 + tabs.length) % tabs.length]);
                            }
                        }}
                    >
                        {_.map(tabs, (t, idx) => {
                            const isSelected = t === activeTab;
                            return (
                                <div
                                    key={idx}
                                    role="tab"
                                    tabIndex={isSelected ? 0 : -1}
                                    aria-selected={isSelected}
                                    aria-label={_.upperFirst(_.lowerCase(t))}
                                    onClick={() => setTab(t)}
                                    onKeyDown={e => {
                                        if (e.key === 'Enter' || e.key === ' ') {
                                            e.preventDefault();
                                            setTab(t);
                                        }
                                    }}
                                    className={`segment ${isSelected ? 'selected' : ''}`}
                                    style={{ width: (100 / tabs.length) + '%' }}
                                >
                                    <div className="title">{_.upperFirst(_.lowerCase(t))}</div>
                                </div>
                            );
                        })}
                        <div
                            className="selection"
                            style={{
                                width: (100 / tabs.length) + '%',
                                left: (_.indexOf(tabs, activeTab) * (100 / tabs.length)) + '%',
                            }}
                            aria-hidden="true"
                        />
                    </div>

                    {activeTab === TENANTS && (
                        <div className="navActions" style={{ order: 0, gap: '8px' }}>
                            <TenantTypeFilter
                                filters={filters}
                                onToggle={toggleFilter}
                                searchRef={searchRef}
                            />
                            <div className={`searchInput ${searchInput.length > 0 ? 'hasInput' : ''}`}>
                                <div className="icon" aria-hidden="true" />
                                <input
                                    ref={searchRef}
                                    value={searchInput}
                                    onChange={e => setSearchInput(e.target.value)}
                                    onBlur={announceTenantSearch}
                                    onKeyDown={e => { if (e.key === 'Enter') announceTenantSearch(); }}
                                    placeholder="Search Tenants"
                                    className="input"
                                    type="text"
                                    aria-label="Search tenants by name"
                                />
                                <div
                                    className="clear"
                                    onClick={() => { setSearchInput(''); announce('Search cleared. Showing all results.'); }}
                                    onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setSearchInput(''); announce('Search cleared. Showing all results.'); } }}
                                    role="button"
                                    tabIndex={searchInput.length > 0 ? 0 : -1}
                                    aria-label="Clear search"
                                    aria-hidden={searchInput.length === 0}
                                />
                            </div>
                        </div>
                    )}

                    {activeTab === AGENTS && (
                        <div className="navActions" style={{ order: 0, gap: '8px' }}>
                            <div className={`searchInput ${agentSearchInput.length > 0 ? 'hasInput' : ''}`}>
                                <div className="icon" aria-hidden="true" />
                                <input
                                    ref={agentSearchRef}
                                    value={agentSearchInput}
                                    onChange={e => setAgentSearchInput(e.target.value)}
                                    onBlur={announceAgentSearch}
                                    onKeyDown={e => { if (e.key === 'Enter') announceAgentSearch(); }}
                                    placeholder="Search Agent or Connector"
                                    className="input"
                                    type="text"
                                    aria-label="Search agents or connectors by name"
                                />
                                <div
                                    className="clear"
                                    onClick={() => { setAgentSearchInput(''); announce('Search cleared. Showing all results.'); }}
                                    onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setAgentSearchInput(''); announce('Search cleared. Showing all results.'); } }}
                                    role="button"
                                    tabIndex={agentSearchInput.length > 0 ? 0 : -1}
                                    aria-label="Clear search"
                                    aria-hidden={agentSearchInput.length === 0}
                                />
                            </div>
                        </div>
                    )}
                </div>
                <div className="separator"></div>
            </div>
            {
                getTabContents()
            }
            <div
                role="status"
                aria-live="polite"
                aria-atomic="true"
                style={{ position: 'absolute', width: 1, height: 1, padding: 0, margin: -1, overflow: 'hidden', clip: 'rect(0,0,0,0)', whiteSpace: 'nowrap', border: 0 }}
            >
                {srMessage}
            </div>
        </div>
    )
}

export default withRouter(SupportTools);
